import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  FormControlLabel,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../api/client';

const MsisdnBlacklistCreatePage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('manual'); // manual | upload
  const [msisdns, setMsisdns] = useState(['']);
  const [uploadFile, setUploadFile] = useState(null);
  const [error, setError] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sampleError, setSampleError] = useState('');
  const fileInputRef = useRef(null);
  const sampleUrl = '/api/blacklist/sample';

  const isValidMsisdn = (value) => /^[0-9]{1,20}$/.test(value);

  const handleAddField = () => setMsisdns((prev) => [...prev, '']);

  const handleRemoveField = (index) => {
    setMsisdns((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleFieldChange = (index, value) => {
    setMsisdns((prev) => prev.map((v, idx) => (idx === index ? value : v)));
  };

  const resetStates = () => {
    setError('');
    setUploadError('');
    setSuccessMessage('');
    setSampleError('');
  };

  const handleManualSubmit = async () => {
    resetStates();
    const sanitized = msisdns.map((v) => v.trim()).filter(Boolean);
    if (!sanitized.length) {
      setError('Please enter at least one MSISDN.');
      return;
    }
    const invalid = sanitized.find((v) => !isValidMsisdn(v));
    if (invalid) {
      setError('MSISDN must be numeric and under 20 characters.');
      return;
    }
    setLoading(true);
    try {
      const resp = await apiFetch('/api/blacklist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ msisdns: sanitized }),
        parseAs: 'raw'
      });
      const contentType = resp.headers.get('Content-Type') || '';
      if (contentType.includes('application/json')) {
        const data = await resp.json();
        if (!resp.ok || (data.resultCode && data.resultCode !== 200 && data.resultCode !== 0)) {
          throw new Error(data.resultDesc || 'Failed to add blacklist');
        }
        setSuccessMessage('MSISDNs added successfully.');
        setMsisdns(['']);
        setTimeout(() => navigate('/msisdn-blacklist'), 800);
      } else {
        // assume XLSX of failed rows
        const blob = await resp.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'blacklist_errors.xlsx';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        setError('Some rows failed. See downloaded file for details.');
      }
    } catch (err) {
      setError(err.message || 'Failed to add blacklist');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    resetStates();
    if (!uploadFile) {
      setUploadError('Please choose a file to upload.');
      return;
    }
    setLoading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    try {
      const resp = await apiFetch('/api/blacklist/upload', {
        method: 'POST',
        body: formData,
        parseAs: 'raw'
      });
      const contentType = resp.headers.get('Content-Type') || '';
      if (contentType.includes('application/json')) {
        const data = await resp.json();
        if (!resp.ok || (data.resultCode && data.resultCode !== 200 && data.resultCode !== 0)) {
          throw new Error(data.resultDesc || 'Failed to upload blacklist');
        }
        setSuccessMessage('Upload succeeded.');
        setUploadFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setTimeout(() => navigate('/msisdn-blacklist'), 800);
      } else {
        const blob = await resp.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'blacklist_errors.xlsx';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        setUploadError('Some rows failed. See downloaded file for details.');
      }
    } catch (err) {
      setUploadError(err.message || 'Failed to upload blacklist');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2
        }}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="/"
            sx={{ fontSize: 12, mb: 1, color: 'text.secondary' }}
          >
            <Link underline="hover" color="inherit" href="#">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="#">
              Management
            </Link>
            <Typography color="text.primary">MSISDN Blacklist</Typography>
          </Breadcrumbs>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            New Blacklist
          </Typography>
          <Typography color="text.secondary">
            Add MSISDNs via manual entry or upload file.
          </Typography>
        </Box>
        <Button variant="text" onClick={() => navigate('/msisdn-blacklist')}>
          Back to list
        </Button>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 3 }, boxShadow: '0px 24px 48px rgba(15, 23, 42, 0.05)' }}>
        <Stack spacing={2.5}>
          <RadioGroup
            row
            value={mode}
            onChange={(e) => {
              setMode(e.target.value);
              resetStates();
            }}
          >
            <FormControlLabel value="manual" control={<Radio />} label="Add MSISDN directly" />
            <FormControlLabel value="upload" control={<Radio />} label="Upload file" />
          </RadioGroup>

          {mode === 'upload' ? (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                  sx={{ minWidth: 160 }}
                >
                  Choose File
                  <input
                    ref={fileInputRef}
                    hidden
                    type="file"
                    accept=".csv,.txt,.xls,.xlsx"
                    onChange={(e) => {
                      setUploadFile(e.target.files?.[0] || null);
                      resetStates();
                    }}
                  />
                </Button>
                <Typography color="text.secondary">
                  {uploadFile ? uploadFile.name : 'No file selected'}
                </Typography>
              </Stack>
              <Typography variant="body2">
                <Link
                  href={sampleUrl}
                  underline="hover"
                  onClick={(e) => {
                    e.preventDefault();
                    setSampleError('');
                    apiFetch(sampleUrl, { parseAs: 'blob' })
                      .then((resp) => resp.blob())
                      .then((blob) => {
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = 'blacklist-sample.xlsx';
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                      })
                      .catch((err) => setSampleError(err.message || 'Failed to download sample'));
                  }}
                >
                  Download sample file
                </Link>
                {sampleError && (
                  <Typography color="error" variant="body2">
                    {sampleError}
                  </Typography>
                )}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={handleUpload}
                  disabled={loading}
                  startIcon={<UploadIcon />}
                >
                  Upload
                </Button>
              </Stack>
              {uploadError && <Alert severity="error">{uploadError}</Alert>}
              {successMessage && <Alert severity="success">{successMessage}</Alert>}
            </Stack>
          ) : (
            <Stack spacing={2}>
              {msisdns.map((value, idx) => (
                <Stack
                  key={`msisdn-${idx}`}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <TextField
                    label={`MSISDN ${idx + 1}`}
                    value={value}
                    onChange={(e) => handleFieldChange(idx, e.target.value)}
                    fullWidth
                    inputProps={{ maxLength: 20 }}
                  />
                  {msisdns.length > 1 && (
                    <IconButton
                      aria-label="remove"
                      color="error"
                      onClick={() => handleRemoveField(idx)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Stack>
              ))}
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddField}
                sx={{ alignSelf: 'flex-start' }}
              >
                Add another
              </Button>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={handleManualSubmit} disabled={loading}>
                  Submit
                </Button>
                <Button variant="text" onClick={() => navigate('/msisdn-blacklist')}>
                  Cancel
                </Button>
              </Stack>
              {error && <Alert severity="error">{error}</Alert>}
              {successMessage && <Alert severity="success">{successMessage}</Alert>}
            </Stack>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
};

export default MsisdnBlacklistCreatePage;
